import { Injectable, inject } from '@angular/core';
import { filter, map, take, takeUntil, timer } from 'rxjs';
import { DialogService } from '@shared/components/dialog/dialog.service';
import { JobGeneralActions } from '@data/job-general/job-general.actions';
import { IStopInput, Stop } from '@data/stop/stop.model';
import { StopQuery } from '@data/stop/stop.query';
import { MatchingSession } from '@data/session/session.model';
import { IonosFacadeService } from '@core/services/upload/ionos-facade.service';
import type {
  StopDialogComponent,
  StopDialogData,
  StopDialogResult,
  StopImageContext,
} from '../components/stop-dialog/stop-dialog.component';

/**
 * Opens the organizer Stop create/edit dialog. Drop-in replacement for
 * {@link CompanyDialogHandler} — the callback receives a single `IStopInput` (instead of the
 * old `{ attendant, company }`), which the caller dispatches via `StopActions.create/update`.
 * Images picked in the create dialog come back alongside it and are uploaded here, once the
 * created Stop appears in the store.
 *
 * The component is pulled in with a dynamic import on purpose: a static one puts
 * `SharedModule -> table -> context-menu -> stop-menu-options -> this handler -> StopDialogComponent
 * -> FileElementsModule -> SharedModule` into a cycle, and at runtime FileElementsModule then sees
 * `SharedModule === undefined`, which corrupts the module scope of everything it declares
 * (ImageSelectorComponent throws "Cannot read properties of undefined (reading 'ɵcmp')").
 */
@Injectable({ providedIn: 'root' })
export class StopDialogHandler {
  private dialog = inject<DialogService<StopDialogComponent, StopDialogResult>>(DialogService);
  private stopQuery = inject(StopQuery);
  private upload = inject(IonosFacadeService);

  async openCreateStopDialog(
    session: MatchingSession,
    callback: (input?: IStopInput) => void,
  ): Promise<void> {
    JobGeneralActions.loadJobGeneral();
    this.dialog.openWithCallback({
      type: await StopDialogHandler.component(),
      dialogData: { session } as StopDialogData,
      confirmCallback: (result) => {
        if (!result) return;
        // Snapshot before dispatching: the create is fire-and-forget through the effect, so the
        // only handle on the new Stop is the id that shows up in the store afterwards.
        const known = new Set(this.stopQuery.getAll().map((it) => it.id));
        callback(result.input);
        this.uploadOnceCreated(known, result.images);
      },
    });
  }

  async openEditStopDialog(
    session: MatchingSession,
    stop: Stop,
    callback: (input?: IStopInput) => void,
  ): Promise<void> {
    JobGeneralActions.loadJobGeneral();
    this.dialog.openWithCallback({
      type: await StopDialogHandler.component(),
      dialogData: { session, stop, edit: true } as StopDialogData,
      confirmCallback: (result) => (result ? callback(result.input) : undefined),
    });
  }

  /**
   * Logo/banner picked in the create dialog: there is no Stop to upload them to until the create
   * effect stored one, so wait for the first id that was not in the store before and upload then.
   *
   * ponytail: "first unknown id" rather than a create response — the dialog dispatches an action
   * and never sees the request. A concurrent `loadStops` could in theory surface another new Stop
   * first; make the effect hand back the created Stop if that ever bites.
   */
  private uploadOnceCreated(known: Set<string>, images: Map<StopImageContext, File>): void {
    if (!images.size) return;
    this.stopQuery
      .selectAll()
      .pipe(
        map((stops) => stops.find((it) => !known.has(it.id))),
        filter((stop): stop is Stop => !!stop),
        take(1),
        takeUntil(timer(60_000)),
      )
      .subscribe((stop) => {
        for (const [context, file] of images) {
          this.upload.uploadDisplayImage('stops', context, stop.id, file).subscribe();
        }
      });
  }

  private static async component(): Promise<typeof StopDialogComponent> {
    const mod = await import('../components/stop-dialog/stop-dialog.component');
    return mod.StopDialogComponent;
  }
}
